'use client'

import Image from 'next/image';
import Link from 'next/link';
import styled from 'styled-components';

import { phone, smallDesktop } from '@/assets/styles/themeConfig';
import { SOCIAL } from '@/utils/variables';
import { products } from './data';

const Main = styled.main`
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(0, 0, 0, 0.05), transparent 32rem),
    linear-gradient(180deg, #ffffff 0%, #f6f4ef 100%);
  color: #111111;

  .content {
    width: 100%;
    max-width: calc(${smallDesktop}px + 6rem);
    margin: 0 auto;
    padding: 1.25rem 1rem 4rem;

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      padding: 1.75rem 1.5rem 5rem;
    }
  }

  .topbar {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;

    .social {
      display: flex;
      align-items: center;
      justify-self: end;
      gap: 0.55rem;

      a {
        color: #111111;
        opacity: 0.45;
        font-size: 1.15rem;
        transition: opacity 0.2s ease;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .hero {
    text-align: center;
    padding: 2.25rem 0 1.4rem;
    
    p {
      max-width: ${phone + 32}px;
      margin: 0 auto;
      font-size: clamp(0.95rem, 1.65vw, 1.12rem);
      line-height: 1.4;
      color: rgba(17, 17, 17, 0.72);
    }
  }

  .note {
    margin: 0 auto 1.5rem;
    padding: 0.7rem 1rem;
    max-width: 48rem;
    border: 1px solid rgba(17, 17, 17, 0.1);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.9);
    text-align: center;
    color: rgba(17, 17, 17, 0.7);
    font-size: 0.95rem;
    line-height: 1.2;
  }

  .grid {
    display: grid;
    gap: 0.85rem;

    @media ${({ theme }) => theme.mediaQuery.phone} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media ${({ theme }) => theme.mediaQuery.tablet} {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media ${({ theme }) => theme.mediaQuery.smallDesktop} {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`;

const Card = styled.a`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  border: 1px solid rgba(17, 17, 17, 0.08);
  border-radius: 0.65rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.96);
  color: inherit;
  text-decoration: none;
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(17, 17, 17, 0.16);
    box-shadow: 0 0.8rem 2rem rgba(17, 17, 17, 0.06);

    .image-overlay {
      opacity: 1;
    }

    .link-chip {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .image-wrap {
    aspect-ratio: 1 / 1;
    padding: 1.2rem;
    background: #fcfcfb;
    border-bottom: 1px solid rgba(17, 17, 17, 0.08);
  }

  .image-frame {
    position: relative;
    width: 100%;
    height: 100%;
    background: #ffffff;
    border-radius: 0.35rem;
  }

  .image-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 0.65rem;
    background: rgba(17, 17, 17, 0.06);
    opacity: 0;
    transition: opacity 0.18s ease;
    pointer-events: none;
  }

  .link-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 0.4rem 1rem rgba(17, 17, 17, 0.12);
    transform: translateY(-4px);
    opacity: 0;
    transition:
      transform 0.18s ease,
      opacity 0.18s ease;
  }

  .placeholder {
    display: grid;
    place-items: center;
    height: 100%;
    padding: 2rem;
    margin: 2rem;
    text-align: center;
    color: rgba(17, 17, 17, 0.45);
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .copy {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 0.8rem 0.85rem 0.85rem;
  }

  h2 {
    margin-bottom: 0;
    min-height: 2.4em;
    font-size: clamp(1rem, 1.25vw, 1.15rem);
    line-height: 1.2;
    letter-spacing: -0.04em;
  }
`;

const TiktokProducts = () => {
  return (
    <Main>
      <div className="content">
        <div className="topbar">
          <div className="social">
            {SOCIAL.map(({ url, icon }, index) => (
              <Link key={index} href={url}>
                <i aria-hidden className={`fab ${icon}`} />
              </Link>
            ))}
          </div>
        </div>

        <section className="hero">
          <h1 className="h2">TikTok Products</h1>
          <p>Curated TikTok Shop finds, tech, and everyday-carry essentials I personally recommend.</p>
        </section>

        <div className="note">
          Note: commissions may be earned from the links below.
        </div>

        <section className="grid">
          {products.map(({ title, href, img }) => (
            <Card key={title} href={href} target="_blank" rel="noreferrer" aria-label={title}>
              <div className="image-wrap">
                <div className="image-frame">
                  {img ? (
                    <Image
                      src={img}
                      alt={title}
                      fill={true}
                      placeholder="blur"
                      sizes="(max-width: 767px) 100vw, (max-width: 1279px) 50vw, 33vw"
                      style={{ objectFit: 'contain' }}
                    />
                  ) : (
                    <div className="placeholder">
                      No Image Found.
                    </div>
                  )}
                  <div className="image-overlay">
                    <span className="link-chip" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M7 17L17 7M9 7H17V15"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className="copy">
                <h2>{title}</h2>
              </div>
            </Card>
          ))}
        </section>
      </div>
    </Main>
  );
};

export default TiktokProducts;
